// @ts-nocheck

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import styled, { css } from 'styled-components';

import { Target, isIOSEmbeddedAndStandalone } from '../../client';
import { NAVBAR_BASE_HEIGHT } from '../../constants/ui/navbar';
import { VOID_REASONS } from '../../constants/voidReasons';
import { useParentTriggererContext } from '../../contexts/ParentTriggererContext';
import { __ } from '../../i18n/l10n';
import theme from '../../styled-components/dark';
import { AutoScrollAnchor } from '../../styled-components/shared';
import { LabelConfigurations } from '../../types/betGroup';
import type { LabelConfiguration } from '../../types/betGroup';
import { OddButtonThemes } from '../../types/buttons';
import { type MatchViewMode, MatchViewModes } from '../../types/matchView';
import { OutcomeModes } from '../../types/outcome';
import type { OutcomeMode } from '../../types/outcome';
import { SportIds, type SportId } from '../../types/sports';
import LabelWithLine, { BetGroupLabelWithLineClassName } from '../BetGroupLabelWithLine';
import FlagIcon from '../FlagIcon';
import MatchChip, { MatchChipLabelClassName } from '../MatchChip';
import OddButton from '../OddButton';
import PercentDistribution from '../PercentDistribution';
import PlayerWidgetButton from '../PlayerWidgetButton';

const Wrap = styled.div`
  position: relative;
`;

const StyledStyledAutoScrollAnchor = styled(AutoScrollAnchor)`
  top: calc(${Target.__MOBILE__ ? -(NAVBAR_BASE_HEIGHT * (isIOSEmbeddedAndStandalone ? 0.75 : -0.25)) : 0}em - 2.5em);
`;

const WrapTitle = styled.div`
  margin-right: 0.6em;
`;

const FlagLabelWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const StyledFlagIcon = styled(FlagIcon)`
  ${({ $isEvenBiggerFlag, $isBiggerFlag }) => {
    if ($isEvenBiggerFlag) {
      return css`
        width: 3em;
        height: 3em;
      `;
    }
    if ($isBiggerFlag) {
      return css`
        width: 2.5em;
        height: 2.5em;
      `;
    }

    return css`
      width: 1.4em;
      height: 1.4em;
    `;
  }}
  border-radius: 0.3em;
  margin-right: 0.6em;
`;

const StyledLabelWithLine = styled(LabelWithLine)`
  color: ${theme.BetGroupOutcome.color};
  flex: 1;
  transform: translate3d(0, 0, 0);

  .${BetGroupLabelWithLineClassName} {
    background-color: ${theme.BetGroupOutcome.lineColor};
    min-width: 1em;
  }
  .label-text {
    padding-right: 0.5em;
  }
`;

const WrapOdd = styled.div``;

const StyledPercentDistribution = styled(PercentDistribution)`
  margin-top: 0.2em;
`;

const EmptyPercentDistribution = styled(StyledPercentDistribution)`
  visibility: hidden;
`;

const LeftColumn = styled.div`
  display: flex;
  width: 100%;
  margin-right: ${({ $playerTrackerInfos }) => ($playerTrackerInfos ? '0.5em' : '0')};
  align-items: center;
`;

const VoidReasonChipWrapper = styled.div`
  margin-bottom: 2px;
  text-align: center;
`;

const VoidReasonChip = styled(MatchChip)`
  ${({ $isFromLineUp }) =>
    $isFromLineUp
      ? css`
          transform: translate(-50%, -50%);
          margin-left: 50%;

          & .${MatchChipLabelClassName} {
            font-weight: normal;
          }
        `
      : null}
`;

const PlayerTrackerWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
`;

const Starter = styled.div`
  color: ${theme.BetGroupOutcome.starterColor};
  margin: 0 0.5em;
`;

type PlayerTrackerInfos = {
  playerSrId: number;
  playerTeam: 1 | 2;
  matchId: number;
};
export type BetGroupOutcomePropsType = {
  className?: string;
  anchorId?: string;
  flag?: string;
  title: string;
  oddId: number;
  value: number;
  mode: OutcomeMode;
  percentDistribution: number | null;
  addItem: () => void;
  delItem: () => void;
  selected: boolean;
  isForbidden: boolean;
  labelConfiguration: LabelConfiguration;
  playerTrackerInfos: PlayerTrackerInfos;
  isFromLineUp?: boolean;
  uuid?: string;
  hasOccured?: boolean;
  voidReason?: number;
  isEnded: boolean;
  matchViewMode?: MatchViewMode;
  futureBetIsSuspended?: boolean;
  sportId: SportId;
  isBiggerFlag?: boolean;
  showStarterLabel: boolean;
  starter?: boolean;
  displayMMOddsValue?: boolean;
  hotNbUsers?: number;
};

const BetGroupOutcome = ({
  className,
  anchorId,
  flag,
  title,
  value,
  mode,
  percentDistribution,
  addItem,
  delItem,
  selected,
  isForbidden,
  labelConfiguration,
  playerTrackerInfos,
  oddId,
  uuid,
  isFromLineUp = false,
  hasOccured = null,
  voidReason = null,
  isEnded,
  matchViewMode = MatchViewModes.CLASSIC,
  futureBetIsSuspended = false,
  sportId,
  isBiggerFlag = false,
  showStarterLabel,
  starter,
  displayMMOddsValue,
  hotNbUsers,
}: BetGroupOutcomePropsType) => {
  const { parentTriggerer } = useParentTriggererContext();
  const initialized = useRef(false);

  const onOddButtonClick = useCallback(
    (e) => {
      if (e) {
        e.stopPropagation();
      }
      if (!selected && mode === OutcomeModes.PLAY) {
        addItem();
      } else if (selected) {
        delItem();
      }
    },
    [mode, selected, addItem, delItem],
  );

  const onOddButtonClickRef = useRef();
  useEffect(() => {
    onOddButtonClickRef.current = onOddButtonClick;
  }, [onOddButtonClick]);

  useEffect(() => {
    if (parentTriggerer !== null && initialized.current) {
      onOddButtonClickRef.current?.();
    }
  }, [parentTriggerer]);

  useEffect(() => {
    initialized.current = true;
  }, []);

  const buttonTheme = useMemo(() => {
    if (isEnded) {
      if (isFromLineUp) {
        return hasOccured ? OddButtonThemes.GREEN : OddButtonThemes.BLUE;
      }

      return hasOccured ? OddButtonThemes.OUTLINE_GREEN : OddButtonThemes.OUTLINE_BLUE;
    }

    return null;
  }, [hasOccured, isEnded, isFromLineUp]);

  return (
    <Wrap className={className}>
      {!isFromLineUp ? (
        <LeftColumn $playerTrackerInfos={playerTrackerInfos}>
          {labelConfiguration === LabelConfigurations.OUTSIDE_BUTTON ? (
            <WrapTitle className="bet-group-outcome-title">
              <FlagLabelWrapper>
                {flag ? (
                  <StyledFlagIcon
                    flagId={flag}
                    $isEvenBiggerFlag={sportId === SportIds.FORMULA_1}
                    $isBiggerFlag={isBiggerFlag}
                  />
                ) : null}
                <StyledLabelWithLine label={title} />
              </FlagLabelWrapper>
              {percentDistribution !== null ? <EmptyPercentDistribution completeness={0} /> : null}
            </WrapTitle>
          ) : null}
          {playerTrackerInfos ? (
            <PlayerTrackerWrapper>
              <Row>
                {starter && showStarterLabel ? <Starter>{__('Starter')}</Starter> : null}
                <PlayerWidgetButton
                  playerTeam={playerTrackerInfos.playerTeam}
                  playerSrId={playerTrackerInfos.playerSrId}
                  matchId={playerTrackerInfos.matchId}
                />
              </Row>
              {percentDistribution !== null ? <EmptyPercentDistribution completeness={0} /> : null}
            </PlayerTrackerWrapper>
          ) : null}
        </LeftColumn>
      ) : null}
      <WrapOdd className="bet-group-outcome-odd">
        {voidReason && VOID_REASONS[voidReason] ? (
          <VoidReasonChipWrapper className="bet-group-outcome-odd-chip">
            <VoidReasonChip size={isFromLineUp ? 'medium' : 'small'} color="yellow" $isFromLineUp={isFromLineUp}>
              {__(VOID_REASONS[voidReason])}
            </VoidReasonChip>
          </VoidReasonChipWrapper>
        ) : null}
        <OddButton
          mode={mode}
          onClick={!isFromLineUp ? onOddButtonClick : undefined}
          title={labelConfiguration === LabelConfigurations.INSIDE_BUTTON && !isFromLineUp ? title : ''}
          value={value}
          isForbidden={isForbidden}
          selected={selected}
          oddId={oddId}
          uuid={uuid}
          theme={buttonTheme}
          disableClick={isEnded}
          matchViewMode={matchViewMode}
          futureBetIsSuspended={futureBetIsSuspended}
          sportId={sportId}
          displayMMOddsValue={displayMMOddsValue}
          hotNbUsers={hotNbUsers}
        />
        {percentDistribution !== null ? (
          <StyledPercentDistribution completeness={percentDistribution} height={1} hasColor />
        ) : null}
      </WrapOdd>
      {anchorId ? <StyledStyledAutoScrollAnchor data-anchorid={anchorId} /> : null}
    </Wrap>
  );
};

export default React.memo<unknown>(BetGroupOutcome);
